export type TCoordinates = {
	x0: number
	y0: number
	x1: number
	y1: number
}

export type TCategory = {
	id: number
	name: string
	model_label: string
}

export type TSuperCategory = {
	id: number
	name: string | "Countertops"
}

export type TSelectedModelNumber = {
	id: number
	model_number: string
	image_from_stevens_catalogue: string | null
	dimensions: {
		width: string[]
		height: string[]
		depth: string[]
		min_size: number
		avg_size: number
		max_size: number
	}
}

export type TErefEstimationConfig = {
	eref_id: number
	series_override_id: number | null
	has_locks: boolean
	has_slope_top: boolean
	has_separate_base: boolean
	case_color_id: number | null
	countertop_series_and_colors_id: number | null
	delivery_date: { id: number; date: string } | null
}

export type TErefStatus = "work_in_progress" | "reviewed" | "in_quotation"

export type TEref = {
	id: number
	version: number
	elevation_number: string | null
	page_name: string
	page_number: number
	coordinates: TCoordinates
	estimation_config?: TErefEstimationConfig
	status?: TErefStatus
	estimation_config_comment?: string | null
}

export type TElevationNumber = {
	id: number
	version?: number
	ecall_id?: number
	elevation_number: string
	eref_id: number | null
	reference_label?: string | null
}

export type TEcall = {
	id: number
	version: number
	page_name: string
	page_number: number
	location_page_name: string | null
	coordinates: TCoordinates
	room_name: string | null
	elevation_numbers: TElevationNumber[]
}

export type TFurnitureEstimationConfig = {
	furniture_id: number
	number_of_fillers: number
	number_of_finishing_ends: number
	lock_count: number | null
	has_locks: boolean
	adjusted_shelves: number | null
}

export type TFurnitureStatus = "work_in_progress" | "reviewed" | "in_quotation"

export const STATUS_LABEL: Record<TErefStatus | TFurnitureStatus, string> = {
	work_in_progress: "Need review",
	reviewed: "Reviewed",
	in_quotation: "In quotation",
}

export type TFurnitureFixedPriceConfig = {
	fixed_price: number
	name: string | null
}

export type TFurniture = {
	id: number
	version: number
	status?: TFurnitureStatus
	page_number: number
	eref_id: number | null
	coordinates: TCoordinates
	subclass_description_from_ml: string
	detected_model_numbers: string[]
	selected_model_number: TSelectedModelNumber | null
	height: number | null
	width: number | null
	depth: number | null
	estimation_config?: TFurnitureEstimationConfig | null
	estimation_config_comment?: string | null
	fixed_price_config?: TFurnitureFixedPriceConfig | null
	category: TCategory | null
	super_category: TSuperCategory | null
	top_level_category: TSuperCategory | null
}

export type TPageElevationData = {
	page_number: number
	page_name: string | null
	erefs: TEref[]
	ecalls: TEcall[]
	furniture: TFurniture[]
}

export type TErefWithFurniture = TEref & {
	furniture: TFurniture[]
}

export type TConnectFurnitureRequest = {
	eref_version: number
	furniture: { id: number; version: number }[]
}

export type TUpdateErefRequest = {
	version: number
	elevation_number?: string
	page_name?: string
	page_number?: number
	x0?: number
	y0?: number
	x1?: number
	y1?: number
}

/** Full ecall returned by endpoints that disconnect it; mirrors `TEcall` plus a `quotation_id`. */
export type TDisconnectedEcall = TEcall & {
	quotation_id?: number
}

/**
 * Response of editing an eref (e.g. its elevation number). Embeds the updated
 * eref plus the ecalls / ecall_elevations the backend disconnected from it,
 * each carrying its post-disconnection `version`. Both lists are empty when
 * nothing was disconnected.
 */
export type TUpdateErefResponse = TEref & {
	disconnected_ecalls: TDisconnectedEcall[]
	disconnected_ecall_elevations: TElevationNumber[]
}

export type TUpdateErefConfigurationRequest = {
	version: number
	series_override_id?: number | null
	has_locks?: boolean
	has_slope_top?: boolean
	has_separate_base?: boolean
	case_color_id?: number | null
	countertop_series_and_colors_id?: number | null
	delivery_date_id?: number | null
}

export type TUpdateErefConfigurationResponse = {
	id: number
	version: number
	configuration: Omit<TErefEstimationConfig, "eref_id">
	updated_furniture_list: TFurniture[]
}

export type TUpdateErefStatusRequest = {
	version: number
	status: TErefStatus
}

export type TUpdateFurnitureCategoryRequest = {
	version: number
	related_category_id: number | null
}

export type TUpdateFurnitureFixedPriceRequest = {
	version: number
	fixed_price: number
}

export type TUpdateFurnitureRequest = {
	version: number
	selected_model_number_id?: number | null
	x0?: number
	y0?: number
	x1?: number
	y1?: number
	height?: number | null
	width?: number | null
	depth?: number | null
	name?: string | null
}

export type TUpdateFurnitureConfigRequest = {
	version: number
	lock_count?: number | null
	number_of_fillers?: number
	number_of_finishing_ends?: number
	has_locks?: boolean
	adjusted_shelves?: number | null
}

export type TUpdateFurnitureCommentRequest = {
	version: number
	comment: string
}

export type TUpdateFurnitureStatusRequest = {
	version: number
	status: TFurnitureStatus
}

/**
 * Render order for a single furniture group, returned by the furniture PATCH
 * endpoints after backend `parent_eref_id` is the eref the furnitures
 * belong to, or `null` when they are not connected to an eref. The order is
 * decided server-side; the client must render `furnitures_ids_in_order` as-is.
 */
export type TFurnitureOrdering = {
	page_number: number
	parent_eref_id: number | null
	furnitures_ids_in_order: number[]
}

/**
 * Response shape of `PATCH /furniture/{id}/` and
 * `PATCH /furniture/{id}/related-category/` after backend #1767: the updated
 * furniture plus the backend-decided render order for its affected group.
 */
export type TUpdateFurnitureResponse = {
	this_furniture: TFurniture
	ordering: TFurnitureOrdering
}

/**
 * Body for `POST /quotations/{id}/furniture/{furnitureId}/duplicate/` — creates a new
 * furniture from the source identified in the URL. Only the new top-left (`x0`, `y0`) and
 * target `page_number` are sent; the backend derives `x1`/`y1` from the source's size.
 *
 * `eref_id` + `eref_version` are sent together ONLY when pasting onto the source eref's own
 * page (erefs are page-bound). When omitted the new furniture is created unassigned
 * (`eref_id = null`). When supplied, `eref_id` must equal the source's `eref_id` and
 * `eref_version` must match the source eref's current version; the backend links the copy
 * and bumps that eref's version by 1.
 */
export type TDuplicateFurnitureRequest = {
	x0: number
	y0: number
	page_number: number
	eref_id?: number
	eref_version?: number
}

/**
 * Response of the furniture duplicate endpoint: the created furniture (`this_furniture`), the
 * backend-decided render order for its group (`ordering`, same shape as the PATCH responses),
 * plus — when the copy was linked to an eref — that eref at its incremented version.
 */
export type TDuplicateFurnitureResponse = {
	this_furniture: TFurniture
	ordering: TFurnitureOrdering
	updated_eref?: TEref
}

export type TUpdateErefCommentRequest = {
	version: number
	comment: string
}

export type TUpdateEcallRequest = {
	version: number
	x0?: number
	y0?: number
	x1?: number
	y1?: number
	room_name?: string
	page_name?: string
	page_number?: number
}

/**
 * An eref disconnected from an ecall as a side effect of editing the ecall's
 * page or one of its elevation numbers. The backend bumps the eref's version on
 * disconnection; we only need `id` + `version` to keep the cache in sync (the
 * connection itself lives on the ecall's `elevation_numbers[].eref_id`).
 */
export type TDisconnectedEref = {
	id: number
	version: number
}

export type TUpdateEcallResponse = TEcall & {
	quotation_id?: number
	disconnected_erefs: TDisconnectedEref[]
}

export type TUpdateEcallElevationsResponse = {
	elevations: TElevationNumber[]
	disconnected_erefs: TDisconnectedEref[]
}

export type TCreateEcallElevationRequest = {
	ecall_version: number
	elevation_number: string
	eref_id?: number | null
	eref_version?: number
	reference_label?: string
}

export type TUpdateEcallElevationItem = {
	version: number
	ecall_elevation_id: number
	elevation_number: string
	eref_id?: number | null
	eref_version?: number
	reference_label?: string
}

export type TUpdateEcallElevationsRequest = {
	ecall_version: number
	elevations: TUpdateEcallElevationItem[]
}

export type TAutoConnectEcallElevationRequest = {
	ecall_version: number
}

export type TAutoConnectEcallElevationResponse = {
	ecall_elevation: TAutoConnectedElevation
	eref: TEref
	ecall: TEcall
}

export type TAutoConnectErefEcallsRequest = {
	eref_version: number
}

export type TAutoConnectedElevation = TElevationNumber & { version: number }

export type TAutoConnectedEcall = {
	id: number
	version: number
	connected_elevations: TAutoConnectedElevation[]
}

export type TAutoConnectErefEcallsResponse = {
	eref: TEref
	connected_ecalls: TAutoConnectedEcall[]
}

export type TGroupedPageData = {
	page_number: number
	page_name: string | null
	erefs: TErefWithFurniture[]
	unassignedFurniture: TFurniture[]
	ecalls: TEcall[]
}
